'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** Wall type
//***********************************************************************************
//***********************************************************************************


import { cn_material } from './cn_material';
import { CN_MATERIAL_TYPE_HEAVY_STRUCTURE, CN_MATERIAL_TYPE_INSULATED_STRUCTURE, CN_MATERIAL_TYPE_INSULATOR, CN_MATERIAL_TYPE_LIGHT_STRUCTURE } from './cn_material_type';
import { cn_configuration, cn_configuration_choice, cn_configuration_line, cn_configuration_param, cn_configuration_param_group, cn_configuration_tab } from './cn_configuration';
import { cn_element_type_layer_material } from './cn_element_type_layer_material';
import { cn_opening_type, code_to_label, GLAZING_GAZ_LIST } from './cn_opening_type';
import { cn_md5 } from '../utils/cn_utilities';
import { cn_element_type_visitor } from '../utils/visitors/cn_element_type_visitor';
import { cn_constant, FRAME_MATERIALS, GLAZING_LIST } from './cn_model_constants';

const CN_WALL_TYPE_CATEGORY_LIST = ['generic', 'facade', 'inner', 'expert', 'curtain'];

const CONFIGURATION_INSULATORS = [];

export const WALL_STRUCTURE_LIST = [
    { label: 'Béton', code: 'concrete' },
    { label: 'Parpaing', code: 'block' },
    { label: 'Brique', code: 'brick' },
    { label: 'Pierre', code: 'stone' },
    { label: 'Bois', code: 'wood' },
    { label: 'Acier', code: 'steel' }
];

export class cn_wall_type extends cn_element_type_layer_material {
    constructor(parent) {
        super(parent);
        this.class_name = 'cn_wall_type';
        this.name = '';
        /**
         * @type {'generic' | 'facade'| 'inner' | 'curtain'}
         */
        this.category = 'generic';
        this.free = false;

        //*** Specific for curtain walls */
        this.panel_width = 1;
        this.panel_heights = [0.7, 1.3];
        this.panels = 2;
        this.adapt_panels = false;
        this.frame = 'alu';
        this.frame_quality = true;
        this.glazing = 'double';
        this.glazing_gaz = 'air_16';
        this.parities = ['opaque', 'glazed', 'opaque'];

        //*** Physics: */
        this.user_physics = false;
        this.U = 0;
        this.Uw = 0;
    }

    //***********************************************************************************
    //**** set category
    //***********************************************************************************
    set_category(category) {
        if (this.category == category) return;
        this.category = category;
        this.free = false;
        if (this.category == 'generic') {
            this.layers = [cn_material.material_by_code(this.parent, 'unknown', 0.3)];
            return;
        } else if (this.category == 'facade') {
            this.layers = [];
            this.layers.push(cn_material.material_by_code(this.parent, 'cement', 0.01));
            this.layers.push(cn_material.material_by_code(this.parent, 'concrete', 0.2));
            this.layers.push(cn_material.material_by_code(this.parent, 'insulator', 0.1));
            this.layers.push(cn_material.material_by_code(this.parent, 'gypsum', 0.01));
            return;
        } else if (this.category == 'inner') {
            this.layers = [];
            this.layers.push(cn_material.material_by_code(this.parent, 'gypsum', 0.01));
            this.layers.push(cn_material.material_by_code(this.parent, 'air', 0.06));
            this.layers.push(cn_material.material_by_code(this.parent, 'gypsum', 0.01));
            return;
        }
        else if (this.category == 'curtain') {
            this.layers = [];
            this.layers = [cn_material.material_by_code(this.parent, 'unknown', 0.10)];
            return;
        }
    }

    //***********************************************************************************
    /**
     * category label
     * @returns {string} displayable category
     */
    get_category_label() {
        if (this.is_expert) {
            return 'Paroi expert'
        }
        if (this.free)
            return 'Séparation fictive';
        if (this.category == 'facade')
            return 'Façade';
        if (this.category == 'inner')
            return 'Cloison';
        if (this.category == 'curtain')
            return 'Mur-rideau';
        return 'Mur';
    }

    //***********************************************************************************
    get_generic_label() {
        return 'Type de ' + this.get_category_label().toLowerCase();
    }

    //***********************************************************************************
    /**
     * Structure label
     * @returns {string} displayable structure
     */
    get_structure_label() {
        let label;
        if (this.free) {
            label = 'Néant';
        } else if (this.category == 'inner') {
            label = 'Plâtre';
        } else if (this.category == 'facade') {
            const structures = this.layers.filter(it => it.type == CN_MATERIAL_TYPE_HEAVY_STRUCTURE || it.type === CN_MATERIAL_TYPE_INSULATED_STRUCTURE);
            label = structures.length ? structures[0].name : 'Néant';
        } else if (this.is_expert) {
            const structures = this.layers.filter(it => it.type == CN_MATERIAL_TYPE_HEAVY_STRUCTURE);
            label = structures.length ? structures.map(it => it.name).join(', ') : 'Néant';
        } else if (this.category == 'curtain') {
            label = cn_constant.code_to_label(this.frame, FRAME_MATERIALS);
        }
        else 
            label = 'Générique';
        return (typeof(label) == 'string')?label:"";
    }

    //***********************************************************************************
    /**
     * Structure label
     * @returns {string} displayable structure
     */
    get_insulation_label() {
        if (this.free)
            return '';

        let label = '';
        let structure = false;
        for (let i in this.layers) {
            let new_label = '';
            if (this.layers[i].type === CN_MATERIAL_TYPE_INSULATED_STRUCTURE)
                new_label = 'Intégrée';
            if ([CN_MATERIAL_TYPE_HEAVY_STRUCTURE, CN_MATERIAL_TYPE_LIGHT_STRUCTURE].includes(this.layers[i].type))
                structure = true;
            if (this.layers[i].type === CN_MATERIAL_TYPE_INSULATOR)
                new_label = (structure) ? 'Intérieure' : 'Extérieure';

            if (new_label != '') {
                if (label != '') label += ' + ';
                label += new_label;
            }
        }

        return label;
    }

    /**
     * Computes and returns U value
     * @returns {number}
     */
    get_U() {
        if (this.free) return 0;
        if (!this.is_expert) {
            if (this.user_physics) return this.U;
            if (this.category == 'generic')
                return 0;
            if (this.category == 'curtain')
                return this.get_opaque_type().Uw;
        }

        return super.get_U();
    }
    /**
         * Computes and returns Uw value (for curtain walls only)
         * @returns {number}
         */
    get_Uw() {
        if (this.category != 'curtain')
            return 0;
        if (this.user_physics) return this.Uw;
        return this.get_opening_type().Uw;
    }

    get_opening_type(force = false) {
        if (this.category != 'curtain') return null;
        if (!this.opening_type) {
            this.opening_type = new cn_opening_type(this);
            this.opening_type.ID = cn_md5(this.ID, 'opening');
            this.opening_type.opening = 'none';
            this.opening_type._date = -1;
        }
        if (force || this.opening_type._date != this._date) {
            this.opening_type.name = 'Baie de ' + this.get_label();
            this.opening_type.width = this.panel_width;
            this.opening_type.height = this.panel_heights[1];
            this.opening_type.frame = this.frame;
            this.opening_type.frame_quality = this.frame_quality;
            this.opening_type.glazing = this.glazing;
            this.opening_type.glazing_gaz = this.glazing_gaz;
            this.opening_type.user_physics = this.user_physics;
            if (this.user_physics) {
                this.opening_type.Uw = this.Uw;
            }
            this.opening_type.compute_physics();
            this.opening_type._date = this._date;
        }
        return this.opening_type;
    }

    get_opaque_type(force = false) {
        if (this.category != 'curtain') return null;
        if (!this.opaque_type) {
            this.opaque_type = new cn_opening_type(this);
            this.opaque_type.ID = cn_md5(this.ID, 'opaque');
            this.opaque_type.category = 'door';
            this.opaque_type.glazing = 'none';
            this.opaque_type._date = -1;
        }
        if (force || this.opaque_type._date != this._date) {
            this.opaque_type.name = 'Opaque de ' + this.get_label();
            this.opaque_type.width = this.panel_width;
            this.opaque_type.height = this.panel_heights[1];
            this.opaque_type.frame = this.frame;
            this.opaque_type.frame_quality = this.frame_quality;
            this.opaque_type.compute_physics();
            this.opaque_type._date = this._date;
        }
        return this.opaque_type;
    }
    //***********************************************************************************
    //**** default
    //***********************************************************************************
    static default_outer_wall(parent) {
        return new cn_wall_type(parent);
    }

    static default_inner_wall(parent) {
        const wt = new cn_wall_type(parent);
        wt.category = 'generic';
        wt.layers = [cn_material.material_by_code(parent, 'unknown', 0.08)];
        return wt;
    }

    static default_free(parent) {
        const wt = new cn_wall_type(parent);
        wt.free = true;
        wt.layers = [];
        wt.category = 'inner';
        return wt;
    }

    //***********************************************************************************
    //**** Clone
    //***********************************************************************************
    clone() {
        const c = new cn_wall_type(this.parent);
        c.name = this.name;
        c.category = this.category;
        c.free = this.free;
        c.layers = [];
        for (var i in this.layers)
            c.layers.push(this.layers[i].clone());

        c.panel_width = this.panel_width;
        c.panel_heights = this.panel_heights.concat([]);
        c.panels = this.panels;
        c.adapt_panels = this.adapt_panels;
        c.frame = this.frame;
        c.frame_quality = this.frame_quality;
        c.glazing = this.glazing;
        c.glazing_gaz = this.glazing_gaz;
        c.parities = this.parities.concat([]);

        c.user_physics = this.user_physics;
        c.U = this.U;
        c.is_expert = this.is_expert;
        c.Uw = this.Uw;
        c.readonly = this.readonly;
        c.extra_data = this.extra_data;
        return c;
    }

    //***********************************************************************************
    //**** keys
    //***********************************************************************************
    model_keys() {
        return ['name', 'category', 'free', 'layers', 'panel_width', 'panel_heights', 'panels', 'adapt_panels', 'frame', 'frame_quality', 'glazing', 'glazing_gaz', 'parities', 'user_physics', 'U', 'Uw', 'is_expert'];
    }

    //***********************************************************************************
    //**** serialize
    //***********************************************************************************
    serialize() {
        return this._serialize(false)
    }

    serialize_building_agnostic() {
        return this._serialize(true)
    }

    _serialize(building_agnostic) {
        const json = this.serialize_element();
        json.class_name = 'cn_wall_type';
        json.name = this.name;
        if (building_agnostic) {
            // Serialize ID only if not building agnostic (otherwise, don't store ID to avoid potential ID conflicts between buildings)
            json.ID = undefined;
        }
        json.category = this.category;
        json.layers = [];
        for (var i in this.layers)
            json.layers.push(this.layers[i]._serialize(building_agnostic));
        json.free = this.free;

        json.panel_width = this.panel_width;
        json.panel_heights = this.panel_heights;
        json.panels = this.panels;
        json.adapt_panels = this.adapt_panels;
        json.frame = this.frame;
        json.frame_quality = this.frame_quality;
        json.glazing = this.glazing;
        json.glazing_gaz = this.glazing_gaz;
        json.parities = this.parities;

        json.user_physics = this.user_physics;
        json.U = this.U;
        json.is_expert = this.is_expert;
        json.Uw = this.Uw;

        json.readonly = this.readonly;
        json.extra_data = this.extra_data;

        return json;
    }

    static unserialize(json, building) {
        var wt = new cn_wall_type(building);
        wt.unserialize_element(json);
        wt.name = json.name;

        if (typeof (json.category) == 'string' && (CN_WALL_TYPE_CATEGORY_LIST.indexOf(json.category) >= 0))
            wt.category = json.category;

        wt.layers = [];
        if (typeof (json.layers) == 'object' && json.layers.length > 0) {
            for (var i in json.layers) {
                var layer = cn_material.unserialize(json.layers[i], building);
                if (layer == null) continue;
                wt.layers.push(layer);
            }
        }
        if (wt.layers.length == 0)
            wt.layers = [cn_material.material_by_code(building, 'unknown', wt.thickness)];

        if (typeof (json.free) == 'boolean')
            wt.free = json.free;

        if (typeof (json.panel_width) == 'number')
            wt.panel_width = json.panel_width;

        if (typeof (json.panel_heights) == 'object')
            wt.panel_heights = json.panel_heights;

        if (typeof (json.panels) == 'number')
            wt.panels = json.panels;

        if (typeof (json.adapt_panels) == 'boolean')
            wt.adapt_panels = json.adapt_panels;

        if (typeof (json.frame) == 'string')
            wt.frame = json.frame;

        if (typeof (json.frame_quality) == 'boolean')
            wt.frame_quality = json.frame_quality;

        if (typeof (json.glazing) == 'string')
            wt.glazing = json.glazing;

        if (typeof (json.glazing_gaz) == 'string')
            wt.glazing_gaz = json.glazing_gaz;

        if (typeof (json.parities) == 'object')
            wt.parities = json.parities;

        if (typeof (json.user_physics) == 'boolean')
            wt.user_physics = json.user_physics;

        if (typeof (json.U) == 'number')
            wt.U = json.U;

        if (typeof (json.is_expert) == 'boolean')
            wt.is_expert = json.is_expert;

        if (typeof (json.Uw) == 'number')
            wt.Uw = json.Uw;

        if (typeof (json.readonly) == 'boolean')
            wt.readonly = json.readonly;

        if (typeof (json.extra_data) == 'object')
            wt.extra_data = json.extra_data;

        return wt;
    }

    //***********************************************************************************
    //**** return label
    //***********************************************************************************
    get_label() {
        if (this.name != '') return this.name;

        if (this.free) {
            return 'Séparation fictive';
        }

        var element_type_name = '';
        if (this.category == 'facade' || this.is_expert)
            element_type_name += 'Mur ' + this.get_structure_label().toLowerCase();
        else if (this.category == 'curtain')
            element_type_name += 'Mur-rideau ' + this.get_structure_label().toLowerCase();
        else if (this.category == 'inner')
            element_type_name += 'Cloison';
        else
            element_type_name += 'Générique';

        if (this.layers.length && this.layers.some(l => l.type === CN_MATERIAL_TYPE_INSULATOR)) {
            const structures_index = this.layers.map((l, i) => l.type === CN_MATERIAL_TYPE_HEAVY_STRUCTURE || l.type === CN_MATERIAL_TYPE_INSULATED_STRUCTURE ? i : -1).filter(i => i >= 0);
            const min = Math.min(...structures_index);
            const max = Math.max(...structures_index);
            const iti = this.layers.some((l, i) => l.type === CN_MATERIAL_TYPE_INSULATOR && i > max);
            const ite = this.layers.some((l, i) => l.type === CN_MATERIAL_TYPE_INSULATOR && i < min);
            if (ite || iti) {
                element_type_name += ' avec ';
                element_type_name += iti && ite ? 'ITR' : iti ? 'ITI' : 'ITE';
            }
        }

        element_type_name += ' ' + (this.thickness * 100).toFixed(0) + 'cm';


        return element_type_name;
    }

    //***********************************************************************************
    //**** Returns description
    //***********************************************************************************

    /**
     * Returns an array of objects describing the type.
     * @returns {{label: string, value?: any, decimals?: number, unit?: string}[]}
     */
    get_description() {
        var description = [];

        description.push({ label: 'Catégorie', value: this.get_category_label() });
        if (this.free) return description;

        var th = { label: 'Epaisseur' };
        th.value = this.thickness * 100;
        th.decimals = 1;
        th.unit = 'cm';
        description.push(th);

        if (this.category == 'generic') {
            if (this.user_physics)
                description.push({ label: 'U', value: this.get_U(), decimals: 3, unit: 'W/m².K' });
        }
        else {
            description.push({ label: 'U', value: this.get_U(), decimals: 3, unit: 'W/m².K' });

            if (this.category == 'curtain') {
                var frame_label = cn_constant.code_to_label(this.frame, FRAME_MATERIALS);
                if (frame_label) {
                    if (this.frame_quality) frame_label += ' récent';
                    else frame_label += ' ancien';
                    description.push({ label: 'Menuiserie', value: frame_label });
                }

                var glazing_label = code_to_label(this.glazing, GLAZING_LIST);
                if (glazing_label) {
                    if (this.glazing == 'double') glazing_label += ' ' + code_to_label(this.glazing_gaz, GLAZING_GAZ_LIST);
                    description.push({ label: 'Vitrage', value: glazing_label });
                }

                return description;
            }

            var layers = { label: 'Matériaux' };
            description.push(layers);
            layers.value = [];

            for (var i in this.layers) {
                layers.value.push(this.layers[i].get_label());
            }
        }

        if (this.extra_data) {
            for (var key in this.extra_data) {
                if (this.extra_data[key].description)
                    description.push(this.extra_data[key].description);
            }
        }

        return description;
    }

    //***********************************************************************************
    //**** draw icon
    //***********************************************************************************
    draw_svg_icon(width, height, fit_box = false) {
        if (this.is_null) return super.draw_svg_icon(width, height);
        let html = '';

        if (this.free) {
            html += '<line x1=\'' + (width / 2) + '\' y1=\'0\' x2=\'' + (width / 2) + '\' y2=\'' + height + '\' style=\'stroke:black; stroke-width: 2px; stroke-dasharray: 5,5;\' />';
            return html;
        }

        if (this.category == 'curtain') {
            if (fit_box) return this._draw_curtain_wall_pattern(width, height);
            return this._draw_svg_curtain_wall(width, height);
        }

        return super.draw_layers(width, height, fit_box);
    }

    /**
     * Switch to expert or non-expert.
     * @param {boolean} v
     */
    set_expert(v) {
        super.set_expert(v);
        // also must set category & free attributes
        this.category = 'facade';
        this.free = false;
    }

    /**
     * Draws curtain wall for svg pattern
     * @param {number} width
     * @param {number} height
     * @returns
     */
    _draw_curtain_wall_pattern(width, height) {
        var html = '';
        var fill_color = cn_constant.code_to_rgbcolor(this.frame,FRAME_MATERIALS);

        html += `<rect x="0" y="0" width="${width}" height="${height}" style="fill:${fill_color};stroke:black" />`;

        const screen_thickness = width;

        var glass_color = '';
        if (this.glazing == 'single') glass_color = 'rgb(250,250,255)';
        else if (this.glazing == 'double') glass_color = 'rgb(220,220,255)';
        else if (this.glazing == 'triple') glass_color = 'rgb(200,200,255)';
        else if (this.glazing == 'none') glass_color = fill_color;
        var glass_thickness = width / 4;
        if (this.glazing == 'double') glass_thickness *= 2;
        else if (this.glazing == 'triple') glass_thickness *= 3;
        else if (this.glazing == 'none') glass_thickness = screen_thickness * 0.9;

        var y = 0;
        var pw = height / this.panels - screen_thickness;
        for (var n = 0; n < this.panels; n++) {
            y += screen_thickness * 0.5;
            html += `<line x1="0" y1="${y}" x2="${width}" y2="${y}" style="stroke:black" />`;
            html += `<line x1="0" y1="${y + pw}" x2="${width}" y2="${y + pw}" style="stroke:black" />`;
            html += `<rect x="${0.5 * (width - glass_thickness)}" y="${y}" width="${glass_thickness}" height="${pw}" style="fill:${glass_color};stroke:black" />`;
            y += screen_thickness * 0.5 + pw;
        }
        return html;
    }

    /**
     * Draw curtain wall for wall type icons
     * @param {number} width
     * @param {number} height
     * @returns
     */
    _draw_svg_curtain_wall(width, height) {
        var ow = this.panel_width * this.panels;
        var oh = this.panel_heights[0] + this.panel_heights[1] + 1;
        var scale = (ow / oh > width / height) ? width / ow : height / oh;

        var x0 = width / 2 - ow * scale / 2;
        var x1 = width / 2 + ow * scale / 2;
        var y0 = height / 2 - oh * scale / 2;
        var y1 = height / 2 + oh * scale / 2;

        var fill_color = '';
        var fill_color = cn_constant.code_to_rgbcolor(this.frame,FRAME_MATERIALS);

        var glass_color = '';
        if (this.glazing == 'single') glass_color = 'rgb(250,250,255)';
        else if (this.glazing == 'double') glass_color = 'rgb(220,220,255)';
        else if (this.glazing == 'triple') glass_color = 'rgb(200,200,255)';
        else if (this.glazing == 'none') glass_color = fill_color;

        var html = '';
        html += `<rect x="${x0}" y="${y0}" width="${x1 - x0}" height="${y1 - y0}" style="fill:${fill_color};stroke:black" />`;
        var mullion = this.thickness * scale;

        var panel_heights = this.panel_heights.concat([1]);
        for (var h = 2; h >= 0; h--) {
            y0 += 0.5 * mullion;
            var ph = scale * panel_heights[h] - mullion;
            var panel_color = (this.parities[h] == 'glazed') ? glass_color : fill_color;
            for (var n = 0; n < this.panels; n++) {
                html += `<rect x="${x0 + n * scale * this.panel_width + 0.5 * mullion}" y="${y0}" width="${scale * this.panel_width - mullion}" height="${ph}" style="fill:${panel_color};stroke:black" />`;
                if (this.parities[h] == 'partial') panel_color = glass_color;
            }
            y0 += 0.5 * mullion + ph;
        }
        return html;
    }

    //***********************************************************************************
    //**** Get configuration
    //***********************************************************************************
    static configuration() {
        var configuration = new cn_configuration();

        var param_group = new cn_configuration_param_group('Thermique', 'thermics', true);
        configuration.add_param_group(param_group);
        param_group.add_param(new cn_configuration_param('U (sans Rs)', 'U', 0, 10, 'W/m².K', 3, 0.1));
        param_group.add_param(new cn_configuration_param('Uf', 'Uf', 0, 10, 'W/m².K', 3, 0.1));
        param_group.add_param(new cn_configuration_param('Uw', 'Uw', 0, 10, 'W/m².K', 3, 0.1));

        //*** Curtain wall param group */
        var panel_group = new cn_configuration_param_group('Panneaux', 'panels', false);
        configuration.add_param_group(panel_group);
        panel_group.add_param(new cn_configuration_param('Epaisseur', 'thickness', 1, 500, 'mm', 0, 1));
        panel_group.add_param(new cn_configuration_param('Largeur', 'panel_width', 0.1, 10, 'm', 3, 0.1));
        panel_group.add_param(cn_configuration_param.choice_list('Arrondir largeur', 'adapt_panels', ["Non", "Oui"]));
        panel_group.add_param(new cn_configuration_param('Répétition', 'panels', 2, 10, '', 0, 1));

        //***********************************************
        //*** Generic configuration
        var generic = new cn_configuration_tab('Générique', 'generic');
        configuration.add_tab(generic);

        var generic_list = new cn_configuration_line('Mur générique');
        generic.add_line(generic_list);

        generic_list.add_param(new cn_configuration_param('Epaisseur', 'thickness', 1, 200, 'cm', 1, 0.5));

        generic_list.add_choice(new cn_configuration_choice());

        //***********************************************
        //*** Wall configuration
        var walls = new cn_configuration_tab('Murs', 'facade');
        configuration.add_tab(walls);

        var wall_structure = new cn_configuration_line('Structure', 'structure');
        walls.add_line(wall_structure);
        wall_structure.add_param(new cn_configuration_param('Epaisseur', 'thickness', 1, 200, 'cm', 1, 1));
        WALL_STRUCTURE_LIST.forEach(ws => {
            wall_structure.add_choice(new cn_configuration_choice(ws.label, ws.code));
        });

        configuration['insulator_labels'] = ['Minéral', 'Naturel', 'Synthétique', 'Lame d\'air'];
        configuration['insulator_codes'] = ['insulator', 'wood_fiber', 'styrofoam', 'air_layer'];

        var wall_insulation = new cn_configuration_line('Isolant 1', 'insulator1');
        walls.add_line(wall_insulation);
        wall_insulation.add_param(new cn_configuration_param('Epaisseur', 'thickness', 1, 200, 'cm', 1, 1));
        wall_insulation.add_param(cn_configuration_param.choice_list('Type', 'insulator_type', configuration['insulator_labels']));
        wall_insulation.add_choice(new cn_configuration_choice('Aucun', 'none'));
        wall_insulation.add_choice(new cn_configuration_choice('Extérieur', 'outer'));
        wall_insulation.add_choice(new cn_configuration_choice('Intérieur', 'inner'));
        wall_insulation.add_choice(new cn_configuration_choice('Intégré', 'integrated'));

        wall_insulation = new cn_configuration_line('Isolant 2', 'insulator2');
        walls.add_line(wall_insulation);
        wall_insulation.add_param(new cn_configuration_param('Epaisseur', 'thickness', 1, 200, 'cm', 1, 1));
        wall_insulation.add_param(cn_configuration_param.choice_list('Type', 'insulator_type', configuration['insulator_labels']));
        wall_insulation.add_choice(new cn_configuration_choice('Aucun', 'none'));
        wall_insulation.add_choice(new cn_configuration_choice('Extérieur', 'outer'));
        wall_insulation.add_choice(new cn_configuration_choice('Intérieur', 'inner'));

        var wall_outer_facing = new cn_configuration_line('Parement extérieur', 'outer_facing');
        walls.add_line(wall_outer_facing);
        wall_outer_facing.add_param(new cn_configuration_param('Epaisseur', 'thickness', 1, 200, 'cm', 1, 0.5));
        wall_outer_facing.add_choice(new cn_configuration_choice('Aucun', 'none'));
        wall_outer_facing.add_choice(new cn_configuration_choice('Enduit', 'cement'));
        wall_outer_facing.add_choice(new cn_configuration_choice('Bardage', 'tiles'));
        wall_outer_facing.add_choice(new cn_configuration_choice('Bardage ventilé', 'ventilated_tiles'));

        var wall_inner_facing = new cn_configuration_line('Parement intérieur', 'inner_facing');
        walls.add_line(wall_inner_facing);
        wall_inner_facing.add_param(new cn_configuration_param('Epaisseur', 'thickness', 1, 200, 'cm', 1, 0.5));
        wall_inner_facing.add_choice(new cn_configuration_choice('Aucun', 'none'));
        wall_inner_facing.add_choice(new cn_configuration_choice('Plâtre', 'gypsum'));

        //***********************************************
        //*** Inner configuration
        var inner_walls = new cn_configuration_tab('Cloisons', 'inner');
        configuration.add_tab(inner_walls);

        var inner_walls_line = new cn_configuration_line();
        inner_walls.add_line(inner_walls_line);
        inner_walls_line.add_param(new cn_configuration_param('Epaisseur', 'thickness', 1, 200, 'cm', 1, 0.5));
        inner_walls_line.add_choice(new cn_configuration_choice('Cloison plâtre', 'gypsum'));
        inner_walls_line.add_choice(new cn_configuration_choice('Cloison plâtre isolée', 'insulatedgypsum'));
        inner_walls_line.add_choice(new cn_configuration_choice('Séparation fictive', 'free'));

        //***********************************************
        //*** Curtain wall configuration
        var curtain_walls = new cn_configuration_tab('Mur-rideaux', 'curtain');
        configuration.add_tab(curtain_walls);

        var frame_list = new cn_configuration_line('Menuiserie', 'frame');
        curtain_walls.add_line(frame_list);
        FRAME_MATERIALS.forEach(fl => {
            frame_list.add_choice(new cn_configuration_choice(fl.label, fl.code));
        })
        frame_list.clamp_choices(cn_configuration.max_choices);
        frame_list.add_param(cn_configuration_param.choice_list('Type', 'frame_quality', ['ancien', 'récent']));

        var glazing_list = new cn_configuration_line('Vitrage', 'glazing');
        curtain_walls.add_line(glazing_list);

        glazing_list.add_choice(new cn_configuration_choice('Simple', 'single'));
        glazing_list.add_choice(new cn_configuration_choice('Double', 'double'));
        glazing_list.add_choice(new cn_configuration_choice('Triple', 'triple'));
        glazing_list.add_param(cn_configuration_param.choice_list('Lame d\'air', 'glazing_gaz', GLAZING_GAZ_LIST.map(g => g.label), 3));

        var parity_low = new cn_configuration_line('Allège', 'parity_low');
        curtain_walls.add_line(parity_low);
        parity_low.add_choice(new cn_configuration_choice('Opaque', 'opaque'));
        parity_low.add_choice(new cn_configuration_choice('Partielle', 'partial'));
        parity_low.add_choice(new cn_configuration_choice('Vitrée', 'glazed'));
        parity_low.add_param(new cn_configuration_param('Hauteur', 'height', 0, 1000, 'cm', 1, 1));

        var parity_med = new cn_configuration_line('Fenêtre', 'parity_med');
        curtain_walls.add_line(parity_med);
        parity_med.add_choice(new cn_configuration_choice('Opaque', 'opaque'));
        parity_med.add_choice(new cn_configuration_choice('Partielle', 'partial'));
        parity_med.add_choice(new cn_configuration_choice('Vitrée', 'glazed'));
        parity_med.add_param(new cn_configuration_param('Hauteur', 'height', 0, 1000, 'cm', 1, 1));

        var parity_high = new cn_configuration_line('Imposte', 'parity_high');
        curtain_walls.add_line(parity_high);
        parity_high.add_choice(new cn_configuration_choice('Opaque', 'opaque'));
        parity_high.add_choice(new cn_configuration_choice('Partielle', 'partial'));
        parity_high.add_choice(new cn_configuration_choice('Vitrée', 'glazed'));

        //***********************************************
        //*** Default values
        var wt = new cn_wall_type(null);
        wt.set_category('facade');
        wt.fill_configuration(configuration);
        wt.set_category('generic');
        wt.fill_configuration(configuration);
        wt.set_category('inner');
        wt.fill_configuration(configuration);
        wt.set_category('curtain');
        wt.fill_configuration(configuration);

        return configuration
    }

    //***********************************************************************************
    //**** Fill configuration
    //***********************************************************************************
    fill_configuration(config = null) {

        const configuration = (config) ? config : cn_wall_type.configuration();

        var param_group = configuration.get_param_group('thermics');
        param_group.checked = this.user_physics;
        this.get_opening_type(true);
        this.get_opaque_type(true);
        param_group.set_param('U', this.get_U(), this.category != 'curtain');
        param_group.set_param('Uf', this.get_U(), this.category == 'curtain');
        param_group.set_param('Uw', this.get_Uw(), this.category == 'curtain');
        param_group.visible = !this.free && !this.is_expert;

        var panel_group = configuration.get_param_group('panels');
        panel_group.visible = (this.category == 'curtain' && !this.is_expert);

        //*** default to generic
        configuration.selection = 0;

        //*** find proper tab
        const tab_index = configuration.get_tab_index(this.category);
        if (tab_index < 0) return false;
        var configuration_tab = configuration.tabs[tab_index];

        if (this.category == 'generic') {
            configuration.tabs[0].lines[0].get_param('thickness').value = this.thickness * 100;
            return configuration;
        }

        if (this.category == 'facade') {
            var structure = 'concrete';
            var structure_thickness = 0.2;
            var insulator = 'none';
            var facingext = 'none';
            var facingint = 'none';

            var layers = this.layers;
            if (layers.length == 0) return false;

            //find structure layer
            var structure_layer_found = false;

            var structure_line = configuration_tab.get_line('structure');

            var insulator1_line = configuration_tab.get_line('insulator1');
            insulator1_line.set_selection('none');
            insulator1_line.set_param('thickness', 10, false);
            insulator1_line.get_param('insulator_type').visible = false;

            var insulator2_line = configuration_tab.get_line('insulator2');
            insulator2_line.set_selection('none');
            insulator2_line.set_param('thickness', 10, false);
            insulator2_line.get_param('insulator_type').visible = false;

            var first_insulator = null;

            var outer_facing_line = configuration_tab.get_line('outer_facing');
            outer_facing_line.set_selection('none');
            outer_facing_line.set_param('thickness', 1, false);

            var inner_facing_line = configuration_tab.get_line('inner_facing');
            inner_facing_line.set_selection('none');
            inner_facing_line.set_param('thickness', 1, false);

            for (var i = 0; i < layers.length; i++) {
                var insulator_line = (first_insulator == null) ? insulator1_line : insulator2_line;
                var structure_choice = structure_line.get_choice_index(layers[i].code);
                if (structure_choice >= 0) {
                    structure_line.selection = structure_choice;
                    structure_line.set_param('thickness', layers[i].thickness * 100);
                    structure_layer_found = true;
                } else if (layers[i].code == 'insulated_wood') {
                    structure_line.set_selection('wood');
                    structure_line.set_param('thickness', layers[i].thickness * 100);

                    if (first_insulator) {
                        insulator2_line.set_selection('outer');
                        insulator2_line.set_param('thickness', first_insulator.thickness * 100);
                        insulator2_line.set_param('insulator_type', configuration['insulator_codes'].indexOf(first_insulator.code), true);
                        insulator1_line.set_selection('integrated');
                        insulator1_line.get_param('insulator_type').visible = false;
                    } else
                        insulator_line.set_selection('integrated');
                    first_insulator = layers[i];
                    structure_layer_found = true;
                } else if (layers[i].code == 'insulated_steel') {
                    structure_line.set_selection('steel');
                    structure_line.set_param('thickness', layers[i].thickness * 100);

                    if (first_insulator) {
                        insulator2_line.set_selection('outer');
                        insulator2_line.set_param('thickness', first_insulator.thickness * 100);
                        insulator2_line.set_param('insulator_type', configuration['insulator_codes'].indexOf(first_insulator.code), true);
                        insulator1_line.set_selection('integrated');
                        insulator1_line.get_param('insulator_type').visible = false;
                    } else
                        insulator_line.set_selection('integrated');
                    first_insulator = layers[i];
                    structure_layer_found = true;
                } else if (layers[i].code == 'insulated_concrete') {
                    structure_line.set_selection('concrete');
                    structure_line.set_param('thickness', layers[i].thickness * 100);

                    if (first_insulator) {
                        insulator2_line.set_selection('outer');
                        insulator2_line.set_param('thickness', first_insulator.thickness * 100);
                        insulator2_line.set_param('insulator_type', configuration['insulator_codes'].indexOf(first_insulator.code), true);
                        insulator1_line.set_selection('integrated');
                        insulator1_line.get_param('insulator_type').visible = false;
                    } else
                        insulator_line.set_selection('integrated');
                    first_insulator = layers[i];
                    structure_layer_found = true;
                } else if (layers[i].type == CN_MATERIAL_TYPE_INSULATOR) {
                    if (structure_layer_found)
                        insulator_line.set_selection('inner');
                    else
                        insulator_line.set_selection('outer');
                    insulator_line.set_param('thickness', layers[i].thickness * 100);
                    insulator_line.set_param('insulator_type', configuration['insulator_codes'].indexOf(layers[i].code), true);
                    first_insulator = layers[i];
                } else if (layers[i].code == 'cement' || layers[i].code == 'tiles') {
                    if (i == 0) {
                        outer_facing_line.set_selection(layers[i].code);
                        outer_facing_line.set_param('thickness', layers[i].thickness * 100);
                    }
                } else if (layers[i].code == 'air') {
                    if (i == 1 && layers[0].code == 'tiles') {
                        outer_facing_line.set_selection('ventilated_tiles');
                        outer_facing_line.set_param('thickness', (layers[0].thickness + layers[i].thickness) * 100);
                    }
                } else if (layers[i].code == 'gypsum') {
                    if (i == layers.length - 1) {
                        inner_facing_line.set_selection(layers[i].code);
                        inner_facing_line.set_param('thickness', layers[i].thickness * 100);
                    }
                }
            }
            //if (!structure_layer_found) return false;
            configuration.selection = tab_index;

            insulator1_line.get_choice('integrated').visible = (structure_line.get_selection() == 'wood' || structure_line.get_selection() == 'steel' || structure_line.get_selection() == 'concrete');

            return configuration;
        }

        if (this.category == 'inner') {
            configuration.set_selection('inner');
            var configuration_tab = configuration.get_tab('inner');
            var configuration_line = configuration_tab.lines[0];
            var layers = this.layers;
            if (this.free)
                configuration_line.set_selection('free');
            else if (layers.length == 3 && layers[1].code == 'insulator')
                configuration_line.set_selection('insulatedgypsum');
            else
                configuration_line.set_selection('gypsum');
            configuration_line.set_param('thickness', this.thickness * 100, layers.length > 0);
            return configuration;
        }

        if (this.category == 'curtain') {
            panel_group.set_param('thickness', this.thickness * 1000);
            panel_group.set_param('panel_width', this.panel_width);
            panel_group.set_param('panels', this.panels);
            panel_group.set_param('adapt_panels', (this.adapt_panels) ? 1 : 0);
            configuration.set_selection('curtain');
            var configuration_tab = configuration.get_tab('curtain');

            var frame_line = configuration_tab.get_line('frame');
            frame_line.set_clamped_selection(this.frame);
            frame_line.set_param('frame_quality', (this.frame_quality) ? 1 : 0);

            var glazing_line = configuration_tab.get_line('glazing');
            glazing_line.set_selection(this.glazing);
            glazing_line.set_param('glazing_gaz', GLAZING_GAZ_LIST.map(g => g.code).indexOf(this.glazing_gaz), this.glazing == 'double');

            var parity_low = configuration_tab.get_line('parity_low');
            parity_low.set_selection(this.parities[0]);
            parity_low.set_param('height', this.panel_heights[0] * 100);

            var parity_med = configuration_tab.get_line('parity_med');
            parity_med.set_selection(this.parities[1]);
            parity_med.set_param('height', this.panel_heights[1] * 100);

            var parity_high = configuration_tab.get_line('parity_high');
            parity_high.set_selection(this.parities[2]);

            return configuration;
        }

        return null;
    }

    //***********************************************************************************
    //**** Load configuration
    //***********************************************************************************
    load_configuration(configuration, actual_change = false) {
        var configuration_tab = configuration.tabs[configuration.selection];
        this.category = configuration_tab.name;

        this.free = false;
        this.layers = [];

        var param_group = configuration.get_param_group('thermics');
        this.user_physics = param_group.checked;
        if (this.user_physics) {
            if (this.category == 'curtain') {
                this.U = param_group.get_param('Uf').value;
                this.Uw = param_group.get_param('Uw').value;
            } else
                this.U = param_group.get_param('U').value;
        }

        if (this.category == 'generic') {
            var configuration_line = configuration_tab.lines[0];
            this.layers = [cn_material.material_by_code(this.parent, 'unknown', 0.01 * configuration_line.get_param('thickness').value)];
        } else if (this.category == 'facade') {
            var structure_line = configuration_tab.get_line('structure');
            var structure = structure_line.get_selection();
            var structure_thickness = 0.01 * structure_line.get_param('thickness').value;

            var insulator1_line = configuration_tab.get_line('insulator1');
            var insulator1 = insulator1_line.get_selection();
            var insulator1_thickness = 0.01 * insulator1_line.get_param('thickness').value;
            var insulator1_code = configuration['insulator_codes'][insulator1_line.get_param('insulator_type').value];

            var insulator2_line = configuration_tab.get_line('insulator2');
            var insulator2 = insulator2_line.get_selection();
            var insulator2_thickness = 0.01 * insulator2_line.get_param('thickness').value;
            var insulator2_code = configuration['insulator_codes'][insulator2_line.get_param('insulator_type').value];

            var outer_facing_line = configuration_tab.get_line('outer_facing');
            var outer_facing = outer_facing_line.get_selection();
            var outer_facing_thickness = 0.01 * outer_facing_line.get_param('thickness').value;

            var inner_facing_line = configuration_tab.get_line('inner_facing');
            var inner_facing = inner_facing_line.get_selection();
            var inner_facing_thickness = 0.01 * inner_facing_line.get_param('thickness').value;

            if (outer_facing != 'none') {
                if (outer_facing == 'ventilated_tiles') {
                    this.layers.push(cn_material.material_by_code(this.parent, 'tiles', 0.5 * outer_facing_thickness));
                    this.layers.push(cn_material.material_by_code(this.parent, 'air', 0.5 * outer_facing_thickness));
                } else
                    this.layers.push(cn_material.material_by_code(this.parent, outer_facing, outer_facing_thickness));
            }
            if (insulator1 == 'outer')
                this.layers.push(cn_material.material_by_code(this.parent, insulator1_code, insulator1_thickness));

            if (insulator2 == 'outer')
                this.layers.push(cn_material.material_by_code(this.parent, insulator2_code, insulator2_thickness));

            if (insulator1 == 'integrated' && structure == 'wood')
                this.layers.push(cn_material.material_by_code(this.parent, 'insulated_wood', structure_thickness));
            else if (insulator1 == 'integrated' && structure == 'concrete')
                this.layers.push(cn_material.material_by_code(this.parent, 'insulated_concrete', structure_thickness));
            else if (insulator1 == 'integrated' && structure == 'steel')
                this.layers.push(cn_material.material_by_code(this.parent, 'insulated_steel', structure_thickness));
            else
                this.layers.push(cn_material.material_by_code(this.parent, structure, structure_thickness));

            if (insulator1 == 'inner')
                this.layers.push(cn_material.material_by_code(this.parent, insulator1_code, insulator1_thickness));

            if (insulator2 == 'inner')
                this.layers.push(cn_material.material_by_code(this.parent, insulator2_code, insulator2_thickness));

            if (inner_facing != 'none')
                this.layers.push(cn_material.material_by_code(this.parent, inner_facing, inner_facing_thickness));

        } else if (this.category == 'inner') {
            var configuration_line = configuration_tab.lines[0];
            var thickness = configuration_line.get_param('thickness').value * 0.01;
            var inner_wall = configuration_line.get_selection();

            if (inner_wall != 'free') {
                let t;
                if (thickness < 0.001) thickness = 0.08;
                if (thickness < 0.026) t = thickness / 2
                else t = 0.013;

                this.free = false;
                this.layers.push(cn_material.material_by_code(this.parent, 'gypsum', t));

                if (inner_wall == 'gypsum')
                    this.layers.push(cn_material.material_by_code(this.parent, 'air', thickness - 2 * t));
                else
                    this.layers.push(cn_material.material_by_code(this.parent, 'insulator', thickness - 2 * t));

                this.layers.push(cn_material.material_by_code(this.parent, 'gypsum', t));

            } else {
                this.free = true;
            }
        } else if (this.category == 'curtain') {
            param_group = configuration.get_param_group('panels');
            this.panel_width = param_group.get_param('panel_width').value;
            this.panels = param_group.get_param('panels').value;
            this.adapt_panels = param_group.get_param('adapt_panels').value == 1;

            this.layers = [cn_material.material_by_code(this.parent, 'unknown', 0.001 * param_group.get_param('thickness').value)];

            this.frame = configuration_tab.get_line('frame').get_clamped_selection();
            this.frame_quality = (configuration_tab.get_line('frame').get_param('frame_quality').value == 1);

            this.glazing = configuration_tab.get_line('glazing').get_selection();
            if (this.glazing == 'double')
                this.glazing_gaz = GLAZING_GAZ_LIST.map(g => g.code)[configuration_tab.get_line('glazing').get_param('glazing_gaz').value];

            this.parities[0] = configuration_tab.get_line('parity_low').get_selection();
            this.panel_heights[0] = configuration_tab.get_line('parity_low').get_param('height').value / 100;
            this.parities[1] = configuration_tab.get_line('parity_med').get_selection();
            this.panel_heights[1] = configuration_tab.get_line('parity_med').get_param('height').value / 100;
            this.parities[2] = configuration_tab.get_line('parity_high').get_selection();
        }

        if (!this.user_physics) {
            param_group = configuration.get_param_group('thermics');
            param_group.checked = this.user_physics;
            param_group.set_param('U', this.get_U());
        }

        return true;
    }

    /**
     * Accept element type visitor
     *
     * @param {cn_element_type_visitor} element_type_visitor
     */
    accept_visitor(element_type_visitor) {
        element_type_visitor.visit_wall_type(this);
    }

    /**
     * returns true if wall geometry hasn't changed since date
     * @param {number} date
     * @returns {boolean}
     */
    up_to_date_thickness(date) {
        if (this.get_date() <= date) return true;
        if (this.get_date('layers') > date) return false;
        return !this.layers.some(l => this.get_date('thickness') > date);
    }

    vertical_compatible() {
        if (this.free) return false;
        if (this.category == "curtain") return false;
        return true;
    }
}

